import Game from '../../Game';
import ResourceDriverManager from './ResourceDriverManager';

export enum ResourceEvent {
    PROGRESS = 'progress',
    COMPLETE = 'complete',
}

export enum ResourceDriver {
    PRIMARY = 0, // atlas, levels, worlds...
    SECONDARY = 1, // sounds...
    TERTIARY = 2, // avatars...
}

export default class ResourceManager {
    private resources: Map<string, any>;
    private drivers: ResourceDriverManager[];

    constructor() {
        this.resources = new Map<string, any>();
        this.drivers = [
            new ResourceDriverManager(this),
            new ResourceDriverManager(this),
            new ResourceDriverManager(this, {
                concurrency: 5,
                autoStart: true,
                maxAttempts: 3,
            }),
        ];
    }

    public add(
        resourceID: string,
        resource?: string,
        driver?: ResourceDriver
    ): void {
        if (!driver) driver = ResourceDriver.PRIMARY;
        this.drivers[driver].add(resourceID, resource);
    }

    public stop(driver?: ResourceDriver): void {
        if (!driver) driver = ResourceDriver.PRIMARY;
        return this.drivers[driver].stop();
    }

    public async load(driver?: ResourceDriver): Promise<void> {
        if (!driver) driver = ResourceDriver.PRIMARY;
        return this.drivers[driver].load();
    }

    public async wait(resourceID: string): Promise<void> {
        // If already exists and it's loaded resolve the promise.
        if (this.existsAndLoaded(resourceID)) {
            return Promise.resolve();
        }

        // Search in which driver the resource is it, if it's not in any of them reject.
        const driver = this.drivers.find(d => d.isQueued(resourceID));
        if (!driver) {
            return Promise.reject();
        }

        // Make sure that the driver is loading the resources.
        driver.load();

        // Wait for...
        while (
            !this.existsAndLoaded(resourceID) &&
            driver.isQueued(resourceID)
        ) {
            await Game.ticker.setTimeoutPromise(500);
        }

        // Resolve or reject the promise.
        if (this.existsAndLoaded(resourceID)) {
            return Promise.resolve();
        } else {
            return Promise.reject();
        }
    }

    public exists(resourceID: string): boolean {
        return (
            this.resources.has(resourceID) ||
            this.drivers.some(driver => driver.isQueued(resourceID))
        );
    }

    public existsAndLoaded(resourceID: string): boolean {
        return this.resources.has(resourceID);
    }

    public get<T>(resourceID: string): T {
        const resource: any = this.resources.get(resourceID);
        if (resource === undefined) {
            throw new Error(`Resource with id '${resourceID}' not found.`);
        }
        return resource;
    }

    public set(resourceID: string, resource: any): void {
        this.resources.set(resourceID, resource);
        this.drivers.forEach(driver => driver.removeFromQueue(resourceID));
    }

    public getDriver(driver?: ResourceDriver): ResourceDriverManager {
        if (!driver) driver = ResourceDriver.PRIMARY;
        return this.drivers[driver];
    }
}
